/**
 * English: Dashboard
 *
 * What this file covers: The dashboard and its first-run setup checklist.
 *
 * Tone: Encouraging and brief. Tell the business what is happening and what to do next, in a few words.
 *
 * English: English is the reference: every key is added here first, in South African English (organise, colour,
 * cancelled). Every other language falls back to this text for any key it does not have yet.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const dashboard: Catalog = {
  'dashboard.welcome': 'Welcome',
  'dashboard.title': 'Welcome, {{name}}',
  'dashboard.title.anonymous': 'Welcome back',
  'dashboard.verifyBanner.title': 'Verify your email',
  'dashboard.verifyBanner.body': 'Your email isn\'t verified yet. Sending broadcasts is disabled until it is.',
  'dashboard.verifyBanner.cta': 'Verify now →',
  'dashboard.metrics.unread': '{{count}} unread',
  'dashboard.trend.percent': '{{value}}% vs yesterday',
  'dashboard.trend.points': '{{value}} pts vs yesterday',
  'dashboard.trend.flat': 'Steady',
  'dashboard.trend.flat.tooltip': 'Little change on the previous 24 hours.',
  'dashboard.trend.tooltip': '{{current}} in the last 24 hours, against {{previous}} the 24 hours before.',
  'dashboard.metrics.deliveryRate': 'Delivery rate',
  'dashboard.metrics.deliveryHint': 'Of messages sent in the last 24 hours',
  'dashboard.metrics.deliveryNone': 'No messages sent yet',
  'dashboard.viewMessages': 'View messages',
  'dashboard.recent.title': 'Recent conversations',
  'dashboard.recent.openInbox': 'Open inbox →',
  'dashboard.recent.empty': 'No conversations yet.',
  'dashboard.recent.noMessages': 'No messages',
  'dashboard.quickActions.title': 'Quick actions',
  'dashboard.quickActions.description': 'Jump straight to the common tasks.',
  'dashboard.quickActions.addContacts': 'Add contacts',
  'dashboard.quickActions.newTemplate': 'New template',
  'dashboard.quickActions.newBroadcast': 'New broadcast',
  'dashboard.quickActions.buildFlow': 'Build a flow',
  'dashboard.quickActions.attachNumber': 'Attach number',
  'dashboard.quickActions.topUp': 'Top up wallet',
  'setup.title': 'Get set up',
  'setup.progress': '{{done}} of {{total}} complete',
  'setup.dismiss': 'Dismiss',
  'setup.dismissed': 'Setup checklist hidden. You can bring it back in Settings → Appearance.',
  'setup.progress.badge': '{{done}} of {{total}} completed',
  'setup.step.done': 'Completed',
  'setup.step.todo': 'Not done yet',
  'setup.restore.hint': 'Bring back the first-run steps on the dashboard.',
  'setup.open': 'Open',
  'setup.error': 'We could not check your setup progress. Some steps may show as incomplete.',
  'setup.task.number.label': 'Connect a WhatsApp number',
  'setup.task.number.hint': 'Connect your Meta phone number to send and receive messages.',
  'setup.task.number.done': 'Your WhatsApp number is connected.',
  'setup.task.contacts.label': 'Add contacts',
  'setup.task.contacts.hint': 'Import a CSV or add contacts one at a time.',
  'setup.task.contacts.done': 'You have contacts in your account.',
  'setup.task.template.label': 'Create a template',
  'setup.task.template.hint': 'Draft a message template and submit it to Meta for approval.',
  'setup.task.template.done': 'You have created a template.',
  'setup.task.conversation.label': 'Start a conversation',
  'setup.task.conversation.hint': 'Send a message and start engaging with your audience.',
  'setup.task.conversation.done': 'You have an active conversation.',
  'dashboard.subtitle': 'Here\'s what\'s happening with {{org}} today.',
  'dashboard.lastUpdated': 'Last updated {{when}}',
  'dashboard.refresh': 'Refresh',
  'dashboard.loadError': 'Some of this page could not be loaded. The figures below may be incomplete.',
  'dashboard.metrics.unavailable': 'Unavailable',
  'dashboard.metrics.inboxHint': 'Open conversations',
  'dashboard.metrics.contactsHint': 'In your contact book',
  'dashboard.metrics.broadcastsHint': 'Sent to date',
  'dashboard.metrics.walletHint': 'Available credit',
  'dashboard.metrics.spend7d': 'Spent over the last 7 days',
  'dashboard.viewInbox': 'View inbox',
  'dashboard.viewContacts': 'View contacts',
  'dashboard.viewBroadcasts': 'View broadcasts',
  'dashboard.viewWallet': 'View wallet',
  'dashboard.recent.viewAll': 'View all',
  'dashboard.recent.error': 'Conversations could not be loaded.',
  'dashboard.recent.emptyAction': 'Attach a number',
  'dashboard.quickActions.newBroadcast.hint': 'Send a broadcast',
  'dashboard.quickActions.addContacts.hint': 'Import or add contacts',
  'dashboard.quickActions.buildFlow.hint': 'Automate replies',
  'dashboard.quickActions.newTemplate.hint': 'Manage message templates',
  'dashboard.quickActions.attachNumber.hint': 'Connect a WhatsApp number',
  'dashboard.quickActions.topUp.hint': 'Add credit to your wallet',
  'setup.restore': 'Show the setup checklist',
  'dashboard.sendBlock.title': 'Your messages are not being delivered',
  'dashboard.sendBlock.ACCOUNT_NOT_PAYABLE': 'WhatsApp is turning down every message on {{number}} over a billing problem on our side. We are fixing it, and you do not need to do anything at Meta.',
  'dashboard.sendBlock.NUMBER_NOT_REGISTERED': '{{number}} is not registered with WhatsApp for sending, so nothing sent from it is arriving.',
  'dashboard.sendBlock.META_REFUSED': 'WhatsApp is turning down every message on {{number}}.',
  'dashboard.sendBlock.alsoOthers': '{{count}} other numbers are affected too.',
  'dashboard.sendBlock.notCharged': 'You are not charged for a message that is not delivered.',
  'dashboard.sendBlock.action': 'Open Numbers',
  'dashboard.disconnected.title': 'WhatsApp disconnected {{number}}',
  'dashboard.disconnected.action': 'Open Numbers',
  'dashboard.websites.failingTitle': 'A website could not be read',
  'dashboard.websites.failingBody': 'We could not read {{names}} for your knowledge base. Your assistant still answers from what was read before.',
  'dashboard.websites.newTitle': 'New pages on your website',
  'dashboard.websites.newBody': '{{count}} new pages were found when your website was read again. Add them so your assistant knows them.',
  'dashboard.websites.action': 'Open the knowledge base',
  'dashboard.conflicts.title': 'Your knowledge base disagrees with itself',
  'dashboard.conflicts.bodyOne': 'Two documents give different information on one point, so the bot could give the wrong one.',
  'dashboard.conflicts.body': 'Documents give different information on {{count}} points, so the bot could give the wrong one.',
  'dashboard.conflicts.action': 'Settle them',
  'dashboard.metrics.deliveryHintBroadcasts': 'Broadcasts, last 24 hours',
  'dashboard.metrics.broadcastsHintDelivered': 'Sent to date · {{count}} delivered in 24 hours',
  'dashboard.viewDeliveryReport': 'View delivery report',
  'dashboard.sendBlock.ACCOUNT_NOT_PAYABLE.customer': 'WhatsApp is turning down every message on {{number}} because your WhatsApp account has no working payment method in Meta. Add one in Meta\'s payment settings.'
};

export default dashboard;
